import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { AuthService } from "../../core/services/auth.service";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiLink,
  TuiLoader,
  tuiLoaderOptionsProvider,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiFade,
  TuiItemsWithMore,
  TuiPagination,
  TuiStatus,
  TuiStringifyContentPipe,
  TuiSwitch,
  TuiTabs,
} from "@taiga-ui/kit";
import { TuiCell, TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import {
  TuiComboBoxModule,
  TuiInputSliderModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { ForgotResetPasswordService } from "../../core/services/forgot-reset-password.service.service";
import { NurseService } from "../../core/services/nurse.service";
import { INurse, getEmployeeNumber, EMPLOYMENT_TYPE } from "../../model/interface/nurse";
import { DisableUnlessPermissionDirective } from "../../core/directives/disable-unless-permission.directive";

@Component({
  selector: 'app-nurse',
  imports: [
    DisableUnlessPermissionDirective,
    FormsModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiDataListWrapper,
    TuiBreadcrumbs,
    TuiButton,
    TuiDataList,
    TuiPagination,
    TuiDropdown,
    TuiFade,
    TuiTable,
    TuiLink,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiCell,
    TuiStatus,
    TuiItemsWithMore,
    TuiSearch,
    ReactiveFormsModule,
    TuiSwitch,
    CommonModule,
    TuiInputSliderModule,
    TuiTextfieldControllerModule,
    RouterLink,
    TuiComboBoxModule,
    TuiStringifyContentPipe,
    TuiLoader,
    TuiDataListDropdownManager,
  ],
  templateUrl: './nurse.component.html',
  styleUrl: './nurse.component.scss',
  providers: [DatePipe, tuiLoaderOptionsProvider({ size: "xl" })],
})
export class NurseComponent {
  protected readonly breadcrumbs = ["Royal Hospital", "Nurse"];
  protected readonly sizes = ["m"] as const;
  protected open = false;
  protected openDropdownsize: TuiSizeL | TuiSizeS = "s";
  protected openDropdown = false;
  protected size = this.sizes[0];
  isLoading = false;
  nurseData: any;
  paginatedData: INurse[] = [];
  nurseService = inject(NurseService);
  showResults: string = "";
  filters = {
    searchText: "",
    status: null,
    archived: false,
    availability: null,
    employmentType: null as string | null,
  };
  pageIndex = 1;
  pageSize = 10;
  total: number = 6;
  sortField = "createdAt";
  sortOrder: number = -1;
  openMap: { [nurseId: string]: boolean } = {};
  loggedInUserCanEdit = false;
  private authService = inject(AuthService);

  constructor(
    private datePipe: DatePipe,
    private resetPasswordService: ForgotResetPasswordService,
    private alerts: TuiAlertService
  ) { }

  // Form setup
  protected readonly form = new FormGroup({
    search: new FormControl(""),
    select: new FormControl(""),
  });

  protected value = null;

  protected readonly statusItems = [
    { status: "Active", value: true },
    { status: "Inactive", value: false },
  ];

  protected readonly availabilityItems = [
    { status: "Available", value: true },
    { status: "Unavailable", value: false },
  ];

  protected readonly employmentTypeItems = [
    EMPLOYMENT_TYPE.CASUAL,
    EMPLOYMENT_TYPE.PERMANENT,
  ];

  protected stringify(item: { status: string; value: boolean } | null): string {
    if (!item) return "";
    return item.status;
  }

  ngOnInit(): void {
    this.loadNurses();
    this.checkUserPermission();
  }

  loadNurses(): void {
    const payload = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      filters: {
        ...this.filters,
        status: this.filters.status ? (this.filters.status as { value: boolean }).value : null,
        availability: this.filters.availability ? (this.filters.availability as { value: boolean }).value : null,
      },
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    };

    this.isLoading = true;

    this.nurseService.getPagedNurses(payload).subscribe({
      next: (res: any) => {
        if (res && res.length > 0 && res[0].data) {
          this.nurseData = res[0].data;
          this.total = res[0].metadata[0].total;
        } else {
          this.nurseData = [];
          this.total = 0;
        }
      },
      error: (error) => {
        console.error("Error fetching nurse data:", error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  toggleDropdown(nurseId: string): void {
    this.openMap[nurseId] = !this.openMap[nurseId];
  }

  sort(): void {
    this.sortOrder = this.sortOrder === 1 ? -1 : 1;
    this.loadNurses();
  }

  applyFilters(newFilters: any): void {
    this.filters = { ...this.filters, ...newFilters };
    this.loadNurses();
  }


  applySort(): void {
    this.loadNurses();
  }

  onEnter(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.applyFilters({ searchText: this.filters.searchText });
    }
  }

  onPageChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.loadNurses();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = Number(pageSize);
    this.pageIndex = 1;
    this.loadNurses();
  }

  clearSearch(): void {
    this.filters.searchText = "";
    this.filters.status = null;
    this.filters.availability = null;
    this.filters.employmentType = null;
    this.applyFilters({ searchText: this.filters.searchText, status: null });
  }

  getPageCount(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  //Dropdown functinalities
  resetUserPassword(email: any): void {
    const payload = {
      email: email,
    };
    this.resetPasswordService.requestResetPasswordNurse(payload).subscribe({
      next: (response) => {
        this.alerts
          .open("Password reset information sent to nurse's email", {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
      },
      error: (error) => {
        console.error("Error resetting password:", error);
      },
    });
  }

  toggleActive(nurseId: string): void {
    this.isLoading = true;
    this.nurseService.getOneNurseById(nurseId).subscribe((user: any) => {
      if (!user) return;
      const updatedUser = { ...user, active: !user.active };

      this.nurseService.updateNurse(updatedUser).subscribe({
        next: (response) => {
          this.nurseData = response;
          const status = updatedUser.active ? "actived" : "deactivated";
          this.alerts
            .open(`Nurse has been ${status} successfully.`, {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
          this.loadNurses();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error("Error updating nurse data:", error);
          this.alerts
            .open("Failed to update nurse data.", {
              label: "Error",
              appearance: "error",
            })
            .subscribe();
        },
      });
    });
  }

  toggleArchive(nurseId: string): void {
    this.nurseService.getOneNurseById(nurseId).subscribe((user: any) => {
      if (!user) return;
      const updatedUser = { ...user, archived: !user.archived };

      this.nurseService.updateNurse(updatedUser).subscribe({
        next: (response) => {
          // this.nurseData = response;
          const status = updatedUser.archived ? "archived" : "unarchived";
          this.alerts
            .open(`Nurse has been ${status} successfully.`, {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
          this.loadNurses();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error("Error updating nurse data:", error);
          this.alerts
            .open("Failed to update nurse data.", {
              label: "Error",
              appearance: "error",
            })
            .subscribe();
        },
      });
    });
  }

  getNurseEmployeeNumber(nurse: INurse): string {
    return getEmployeeNumber(nurse);
  }

  isEligibleForPermanent(nurse: any): boolean {
    return nurse?.permanentConversionStatus === "Eligible";
  }

  isServicePeriodLessThan6Months(registrationDate: string | Date | null | undefined): boolean {
    if (!registrationDate) return true;
    const start = new Date(registrationDate);
    const end = new Date();
    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();
    if (days < 0) {
      months--;
    }
    if (months < 0) {
      years--;
      months += 12;
    }
    const totalMonths = years * 12 + months;
    return totalMonths < 6;
  }

  formatEmploymentStatus(nurse: any): string {
    const status = nurse?.permanentConversionStatus;
    if (!status || status === "None") return "—";
    if (this.isServicePeriodLessThan6Months(nurse.registrationDate)) return "—";
    if (status === "Eligible") return "Pending";
    return status;
  }

  getEmploymentStatusClass(nurse: any): string {
    const status = nurse?.permanentConversionStatus;
    if (this.isServicePeriodLessThan6Months(nurse.registrationDate)) return "status-none";
    switch (status) {
      case "Eligible":
        return "status-pending";
      case "Pending":
        return "status-pending";
      case "Approved":
        return "status-approved";
      case "Rejected":
        return "status-rejected";
      default:
        return "status-none";
    }
  }

  getServiceDurationColor(registrationDate: string | Date): string {
    if (!registrationDate) return 'grey';
    const now = new Date();
    const regDate = new Date(registrationDate);

    let months = (now.getFullYear() - regDate.getFullYear()) * 12;
    months += now.getMonth() - regDate.getMonth();

    if (months < 3) {
      return '#87CEFA';
    } else if (months < 12) {
      return '#f8e46fff';
    } else {
      return '#affbafff';
    }
  }

  getExperienceLabel(months: number): string {
    if (months <= 3) {
      return "Three months";
    } else if (months <= 6) {
      return "Six months";
    } else if (months <= 12) {
      return "One year";
    } else {
      return `One year`;
    }
  }
  checkUserPermission(): void {
    this.loggedInUserCanEdit = this.authService.hasPermission("NURSE_UPDATE");
  }

  getServicePeriodDisplay(registrationDate: string | Date | null | undefined): string {
    if (!registrationDate) return '—';
    const start = new Date(registrationDate);
    const end = new Date(); // today

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    const totalMonths = years * 12 + months;

    if (totalMonths === 0 && days === 0) {
      return '0 days';
    }

    const monthPart = totalMonths > 0 ? `${totalMonths} month${totalMonths === 1 ? '' : 's'}` : '';
    const dayPart = days > 0 ? `${days} day${days === 1 ? '' : 's'}` : '';

    if (monthPart && dayPart) {
      return `${monthPart} and ${dayPart}`;
    }
    return monthPart || dayPart;
  }
}
